import {
  COVERAGE_REPORT_STATUS_LABEL,
  WHOLE_SHIFT_LABEL,
  type CoverageLookups,
  type CoverageReportFilters,
  type CoverageReportRow,
  type CoverageReportTotals,
  type CoverageSummaryRow,
  fillRateFor,
  fillRateLabel,
} from './coverageReport'
import type { ExportTable } from './reportExport'
import { supervisorLabel } from './supervisorLabel'

const TITLE = 'Daily Coverage Report'

export type CoverageExportInput = {
  from: string
  to: string
  filters: CoverageReportFilters
  lookups: CoverageLookups
  view: 'summary' | 'detailed'
  generatedAt: Date
}

function parametersFor(input: CoverageExportInput, rows: CoverageReportRow[]) {
  const { filters, lookups } = input
  const rate = fillRateFor(rows)
  return [
    { label: 'Date range', value: `${input.from} to ${input.to}` },
    { label: 'View', value: input.view === 'summary' ? 'Summary' : 'Detailed' },
    { label: 'Team', value: filters.teamId ? (lookups.teams.get(filters.teamId) ?? 'Unknown') : 'All' },
    {
      label: 'Supervisor',
      value: filters.supervisorId ? supervisorLabel(filters.supervisorId, lookups) : 'All',
    },
    { label: 'Shift', value: filters.shiftId ? (lookups.shifts.get(filters.shiftId) ?? 'Unknown') : 'All' },
    { label: 'Assignment', value: filters.assignmentId ? (lookups.assignments.get(filters.assignmentId) ?? 'Unknown') : 'All' },
    { label: 'Status', value: filters.status ? COVERAGE_REPORT_STATUS_LABEL[filters.status] : 'All' },
    { label: 'Headcount filled', value: rate.percent === null ? 'No target' : `${fillRateLabel(rate)} (${rate.filled} of ${rate.required})` },
  ]
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No')

export function detailedExportTable(rows: CoverageReportRow[], totals: CoverageReportTotals, input: CoverageExportInput): ExportTable {
  return {
    title: TITLE,
    parameters: parametersFor(input, rows),
    generatedAt: input.generatedAt,
    columns: [
      { header: 'Date', width: 12 },
      { header: 'Team' },
      { header: 'Shift' },
      { header: 'Assignment', width: 22 },
      { header: 'Required', numeric: true },
      { header: 'Scheduled', numeric: true },
      { header: 'Available', numeric: true },
      { header: 'Filled %', numeric: true, percent: true },
      { header: 'On leave', numeric: true },
      { header: 'Detailed out', numeric: true },
      { header: 'Unfilled role' },
      { header: 'Qualification gap' },
      { header: 'Status', width: 28 },
    ],
    rows: rows.map((row) => [
      row.date,
      row.team,
      row.shift,
      row.assignment,
      row.required,
      row.scheduled,
      row.available,
      row.fillPercent,
      row.onLeave,
      row.detailedOut,
      yesNo(row.unfilledRole),
      yesNo(row.qualificationGap),
      COVERAGE_REPORT_STATUS_LABEL[row.status],
    ]),
    totals: {
      label: 'Total',
      values: [null, null, null, null, totals.required, totals.scheduled, totals.available, fillRateFor(rows).percent, totals.onLeave, totals.detailedOut, null, null, null],
      excludeWhere: { column: 3, equals: WHOLE_SHIFT_LABEL },
      fixedColumns: [7],
    },
  }
}

export function summaryExportTable(
  rows: CoverageSummaryRow[],
  totals: CoverageReportTotals,
  input: CoverageExportInput,
  detailRows: CoverageReportRow[],
): ExportTable {
  return {
    title: TITLE,
    parameters: parametersFor(input, detailRows),
    generatedAt: input.generatedAt,
    columns: [
      { header: 'Date', width: 12 },
      { header: 'Required', numeric: true },
      { header: 'Scheduled', numeric: true },
      { header: 'Available', numeric: true },
      { header: 'On leave', numeric: true },
      { header: 'Detailed out', numeric: true },
      { header: 'Unfilled roles', numeric: true },
      { header: 'Qualification gaps', numeric: true },
      { header: 'Filled %', numeric: true, percent: true },
      { header: 'Status', width: 28 },
    ],
    rows: rows.map((row) => [
      row.date,
      row.required,
      row.scheduled,
      row.available,
      row.onLeave,
      row.detailedOut,
      row.unfilledRoles,
      row.qualificationGaps,
      row.fillRate.percent,
      COVERAGE_REPORT_STATUS_LABEL[row.status],
    ]),
    totals: {
      label: 'Total',
      values: [null, totals.required, totals.scheduled, totals.available, totals.onLeave, totals.detailedOut, totals.unfilledRoles, totals.qualificationGaps, fillRateFor(detailRows).percent, null],
      fixedColumns: [8],
    },
  }
}
